/**
 * Trusted Types helpers for page-world subtitle paint.
 *
 * Do NOT rely on the page's `default` policy — YouTube may already own it and
 * sanitize HTML to empty (no throw → blank subtitles, no console error).
 * Use a dedicated `unisubs` policy for cue HTML.
 */

export type TrustedTypesLike = {
  createPolicy: (
    name: string,
    rules: {
      createHTML: (s: string) => string;
      createScriptURL?: (s: string) => string;
      createScript?: (s: string) => string;
    },
  ) => { createHTML: (s: string) => string };
  defaultPolicy?: { createHTML: (s: string) => string };
};

type PolicyHolder = { createHTML: (s: string) => string };

const POLICY_KEY = '__unisubsTrustedHtmlPolicy__';

export function installTrustedTypesPolicies(tt: TrustedTypesLike | undefined = getTrustedTypes()): void {
  if (!tt?.createPolicy) return;

  try {
    tt.createPolicy('default', {
      createHTML: (html) => html,
      createScriptURL: (url) => url,
      createScript: (script) => script,
    });
  } catch {
    // Host (e.g. YouTube) may already own `default`.
  }

  if (!getStoredPolicy()) {
    try {
      storePolicy(tt.createPolicy('unisubs', { createHTML: (html) => html }));
    } catch {
      // Re-inject: policy name already registered; keep prior window holder if any.
    }
  }
}

/** Convert cue HTML for innerHTML under Trusted Types (or passthrough). */
export function toTrustedHtml(html: string): string {
  const policy = getStoredPolicy();
  if (policy) {
    return policy.createHTML(html) as unknown as string;
  }
  return html;
}

function getTrustedTypes(): TrustedTypesLike | undefined {
  return typeof window !== 'undefined'
    ? (window as unknown as { trustedTypes?: TrustedTypesLike }).trustedTypes
    : undefined;
}

function getStoredPolicy(): PolicyHolder | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as Record<string, PolicyHolder | undefined>)[POLICY_KEY];
}

function storePolicy(policy: PolicyHolder): void {
  if (typeof window === 'undefined') return;
  (window as unknown as Record<string, PolicyHolder>)[POLICY_KEY] = policy;
}
